import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { ConversationService } from '../../../../services/conversation.service';
import { ITimelineAttachment } from '../../../../models/timeline.models';

export interface AttachmentPreviewDialogData {
  attachment: ITimelineAttachment;
}

// ─────────────────────────────────────────────────────────────────────────────
// AttachmentPreviewDialogComponent
// ─────────────────────────────────────────────────────────────────────────────
// Previews a message attachment inline (no browser download). Fetches the bytes
// through ConversationService.downloadAttachment (which hits the cached, pre-warmed
// get_attachment endpoint). PDFs render in a plain <iframe> pointed at a blob URL so
// the browser's native PDF viewer (zoom / page navigation / search / print) is used;
// everything else (SheetJS grid for xlsx/csv, <img> for images, text/HTML) goes to the
// shared <app-files-previewer> as a File so it detects the type from the extension.
// ─────────────────────────────────────────────────────────────────────────────
@Component({
  selector: 'app-attachment-preview-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './attachment-preview-dialog.component.html',
  styleUrls: ['./attachment-preview-dialog.component.scss'],
})
export class AttachmentPreviewDialogComponent implements OnInit, OnDestroy {

  // ── Public state ───────────────────────────────────────────────────────────

  /** Single-element File array handed to <app-files-previewer> (non-PDF types). */
  previewData: File[] = [];
  /** Blob URL for the browser's native PDF viewer (set only for PDFs). */
  pdfUrl: SafeResourceUrl | null = null;
  isLoading = true;
  hasError = false;

  // ── Private properties ───────────────────────────────────────────────────────

  private readonly destroy$ = new Subject<void>();
  /** Raw blob: URL backing pdfUrl; kept so it can be revoked on destroy/reload. */
  private pdfObjectUrl: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<AttachmentPreviewDialogComponent>,
    private conversationService: ConversationService,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: AttachmentPreviewDialogData,
  ) {}

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadAttachment();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokePdfUrl();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  get fileName(): string {
    return this.data.attachment?.name ?? '';
  }

  close(): void {
    this.dialogRef.close();
  }

  retry(): void {
    this.loadAttachment();
  }

  // ── Private helpers ──────────────────────────────────────────────────────────

  private loadAttachment(): void {
    const attachment = this.data.attachment;
    this.isLoading = true;
    this.hasError = false;
    this.previewData = [];
    this.revokePdfUrl();

    this.conversationService
      .downloadAttachment(attachment.download_url, attachment.name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ blob, filename, contentType }) => {
          if (this.isPdf(filename, contentType)) {
            // Native browser PDF viewer via a blob URL — gives zoom / paging / search
            // / print for free. Same-origin blob of our own fetched bytes, so no sandbox.
            this.pdfObjectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfObjectUrl);
          } else {
            // Wrap as a File (not the relative URL) so the previewer resolves the type
            // from the filename extension and does not double-prefix BASE_URL_API.
            this.previewData = [new File([blob], filename, { type: contentType })];
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.hasError = true;
        },
      });
  }

  private isPdf(filename: string, contentType: string): boolean {
    return (contentType ?? '').toLowerCase().includes('application/pdf')
      || (filename ?? '').toLowerCase().split('?')[0].endsWith('.pdf');
  }

  private revokePdfUrl(): void {
    if (this.pdfObjectUrl) {
      URL.revokeObjectURL(this.pdfObjectUrl);
      this.pdfObjectUrl = null;
    }
    this.pdfUrl = null;
  }
}
